import {CheckOutlined} from '@ant-design/icons';
import {Form} from 'antd';
import React from 'react';

import {
  isFieldDisabled,
  isFieldVisible,
  isQuestionVisible
} from '../constants/commonInputFormFunction';
import {IField} from '../interface/inputType';

interface AccordionCompletionHeaderProps {
  label: string;
  kpiId?: string | number;
  questions: Array<Record<string, unknown>>;
  validationData?: Record<string, {active?: boolean}>;
}

type CompletionField = Pick<IField, 'fieldName' | 'fieldType'>;

const NON_INPUT_FIELD_TYPES = new Set([
  'heading',
  'heading_1',
  'heading_2',
  'heading_3',
  'heading_4',
  'heading_5',
  'display',
  'displayCalculationInTable',
  'verticalDivider'
]);

const isFilledValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (value instanceof Date) return true;
  if (typeof value === 'object') {
    const objectValue = value as Record<string, unknown>;

    if ('value' in objectValue) {
      return isFilledValue(objectValue.value);
    }

    return Object.keys(objectValue).length > 0;
  }

  return true;
};

const AccordionCompletionHeader: React.FC<AccordionCompletionHeaderProps> = ({
  label,
  kpiId,
  questions,
  validationData
}) => {
  return (
    <Form.Item noStyle shouldUpdate>
      {({getFieldValue}) => {
        const visibleQuestions = (questions ?? []).filter((question) => {
          const shouldShowQuestion = isQuestionVisible(question, getFieldValue);
          if (!shouldShowQuestion) return false;

          if (
            validationData &&
            Array.isArray(question?.field) &&
            question.field.length > 0 &&
            question.field.every(
              (field: IField) =>
                field?.fieldName &&
                validationData[field.fieldName]?.active === false
            )
          ) {
            return false;
          }

          return true;
        });

        const actionableFields: CompletionField[] = visibleQuestions.flatMap(
          (question) => {
            const fields = Array.isArray(question?.field) ? question.field : [];

            return fields.flatMap((field: IField) => {
              if (!field) return [];

              if (field.fieldType === 'inputGrid') {
                const gridFields = field?.formContent?.formFields ?? [];

                return gridFields
                  .filter(
                    (gridField) =>
                      gridField?.fieldName &&
                      !NON_INPUT_FIELD_TYPES.has(gridField.type)
                  )
                  .map((gridField) => {
                    const formattedField: CompletionField = {
                      fieldName: gridField.fieldName,
                      fieldType: gridField.type
                    };

                    return formattedField;
                  });
              }

              if (!field?.fieldName) return [];
              if (validationData?.[field.fieldName]?.active === false)
                return [];
              if (!isFieldVisible(field, getFieldValue)) return [];
              if (NON_INPUT_FIELD_TYPES.has(field.fieldType)) return [];
              if (isFieldDisabled(field, getFieldValue)) return [];

              return [field];
            });
          }
        );

        const isCompleted =
          actionableFields.length > 0 &&
          actionableFields.every((field) =>
            isFilledValue(getFieldValue(field.fieldName))
          );

        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <div>
              {kpiId && (
                <div
                  style={{
                    font: 'normal 600 14px/22px Montserrat'
                  }}
                >
                  {`#${kpiId}`}
                </div>
              )}
              <div
                style={{
                  font: kpiId
                    ? 'normal 500 14px/22px Montserrat'
                    : 'normal 600 14px/22px Montserrat'
                }}
              >
                {label}
              </div>
            </div>
            <CheckOutlined
              style={{
                color: isCompleted ? '#22AF5E' : '#D9D9D9',
                fontSize: '16px'
              }}
            />
          </div>
        );
      }}
    </Form.Item>
  );
};

export default AccordionCompletionHeader;
